import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, ToggleLeft, ToggleRight, Calendar, Percent, Tag, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import Header from '../../../components/ui/Header';
import Footer from '../../../components/Footer';
import { supabase } from '../../../lib/supabase';

const DiscountManagement = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    reason: '',
    discount_percentage: '',
    gst_percentage: '',
    valid_from: '',
    valid_till: '',
    applies_to: 'ALL',
    is_active: true
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('discounts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDiscounts(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.reason.trim()) errors.reason = 'Reason is required';
    if (!formData.discount_percentage || formData.discount_percentage < 0 || formData.discount_percentage > 100) {
      errors.discount_percentage = 'Discount must be between 0 and 100';
    }
    if (formData.gst_percentage && (formData.gst_percentage < 0 || formData.gst_percentage > 100)) {
      errors.gst_percentage = 'GST must be between 0 and 100';
    }
    if (!formData.valid_from) errors.valid_from = 'Start date is required';
    if (!formData.valid_till) errors.valid_till = 'End date is required';
    if (formData.valid_from && formData.valid_till && formData.valid_from > formData.valid_till) {
      errors.valid_till = 'End date must be after start date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const payload = {
        ...formData,
        gst_percentage: formData.gst_percentage || null,
        discount_percentage: parseFloat(formData.discount_percentage)
      };

      console.log('💾 Submitting discount:', {
        isEdit: !!editingDiscount,
        payload,
        editingId: editingDiscount?.id
      });

      if (editingDiscount) {
        const { data, error } = await supabase
          .from('discounts')
          .update(payload)
          .eq('id', editingDiscount.id)
          .select();

        console.log('✅ Update response:', { data, error });
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('discounts')
          .insert([payload])
          .select();

        console.log('✅ Insert response:', { data, error });
        if (error) throw error;
      }

      setShowModal(false);
      resetForm();
      fetchDiscounts();
    } catch (err) {
      console.error('❌ Submit error:', err);
      setFormErrors({ submit: err.message });
    }
  };

  const handleToggleActive = async (discount) => {
    try {
      const { error } = await supabase
        .from('discounts')
        .update({ is_active: !discount.is_active })
        .eq('id', discount.id);

      if (error) throw error;
      fetchDiscounts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this discount?')) return;

    try {
      const { error } = await supabase
        .from('discounts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchDiscounts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (discount) => {
    setEditingDiscount(discount);
    setFormData({
      title: discount.title,
      reason: discount.reason,
      discount_percentage: discount.discount_percentage,
      gst_percentage: discount.gst_percentage || '',
      valid_from: discount.valid_from,
      valid_till: discount.valid_till,
      applies_to: discount.applies_to,
      is_active: discount.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      reason: '',
      discount_percentage: '',
      gst_percentage: '',
      valid_from: '',
      valid_till: '',
      applies_to: 'ALL',
      is_active: true
    });
    setFormErrors({});
    setEditingDiscount(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const isDiscountActive = (discount) => {
    const today = new Date().toISOString().split('T')[0];
    return discount.is_active && today >= discount.valid_from && today <= discount.valid_till;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <RefreshCw className="animate-spin h-12 w-12 text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading discounts...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">Discount Management</h1>
              <p className="text-muted-foreground">Manage discounts and GST for services and consultations</p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus size={20} />
              Create Discount
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6 flex items-center gap-3">
              <AlertCircle className="text-error" size={20} />
              <p className="text-error">{error}</p>
            </div>
          )}

          {/* Discounts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {discounts.map((discount) => (
                <motion.div
                  key={discount.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className={`glass-card p-6 relative ${isDiscountActive(discount) ? 'border-2 border-success' : ''
                    }`}
                >
                  {/* Active Badge */}
                  {isDiscountActive(discount) && (
                    <div className="absolute top-4 right-4">
                      <span className="bg-success text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                        <CheckCircle size={12} />
                        Active Now
                      </span>
                    </div>
                  )}

                  {/* Title */}
                  <h3 className="text-xl font-bold text-foreground mb-2 pr-20">{discount.title}</h3>

                  {/* Reason */}
                  <p className="text-sm text-muted-foreground mb-4">{discount.reason}</p>

                  {/* Details */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Percent size={16} className="text-primary" />
                      <span className="font-semibold">{discount.discount_percentage}% Discount</span>
                    </div>

                    {discount.gst_percentage && (
                      <div className="flex items-center gap-2 text-sm">
                        <Tag size={16} className="text-accent" />
                        <span>{discount.gst_percentage}% GST</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-sm">
                      <Calendar size={16} className="text-muted-foreground" />
                      <span>{discount.valid_from} to {discount.valid_till}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-3 py-1 rounded-full ${discount.applies_to === 'ALL' ? 'bg-primary/10 text-primary' :
                        discount.applies_to === 'WELLNESS' ? 'bg-success/10 text-success' :
                          'bg-accent/10 text-accent'
                        }`}>
                        {discount.applies_to}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-4 border-t border-border">
                    <button
                      onClick={() => handleToggleActive(discount)}
                      className="flex items-center gap-1 text-sm hover:text-primary transition-colors"
                    >
                      {discount.is_active ? (
                        <ToggleRight className="text-success" size={20} />
                      ) : (
                        <ToggleLeft className="text-muted-foreground" size={20} />
                      )}
                      <span>{discount.is_active ? 'Enabled' : 'Disabled'}</span>
                    </button>

                    <button
                      onClick={() => handleEdit(discount)}
                      className="ml-auto p-2 hover:bg-primary/10 rounded-lg transition-colors"
                    >
                      <Edit2 size={16} className="text-primary" />
                    </button>

                    <button
                      onClick={() => handleDelete(discount.id)}
                      className="p-2 hover:bg-error/10 rounded-lg transition-colors"
                    >
                      <Trash2 size={16} className="text-error" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {discounts.length === 0 && (
            <div className="text-center py-16">
              <Tag size={64} className="text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Discounts Yet</h3>
              <p className="text-muted-foreground mb-6">Create your first discount to get started</p>
              <button
                onClick={() => setShowModal(true)}
                className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
              >
                Create Discount
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-background rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-border">
                <h2 className="text-2xl font-bold text-foreground">
                  {editingDiscount ? 'Edit Discount' : 'Create New Discount'}
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Title <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="e.g., New Year Special 2025"
                  />
                  {formErrors.title && <p className="text-error text-sm mt-1">{formErrors.title}</p>}
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Reason <span className="text-error">*</span>
                  </label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    rows="3"
                    placeholder="e.g., Celebrate New Year with special discount"
                  />
                  {formErrors.reason && <p className="text-error text-sm mt-1">{formErrors.reason}</p>}
                </div>

                {/* Discount & GST */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Discount % <span className="text-error">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.discount_percentage}
                      onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="0-100"
                    />
                    {formErrors.discount_percentage && (
                      <p className="text-error text-sm mt-1">{formErrors.discount_percentage}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      GST % (Optional)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.gst_percentage}
                      onChange={(e) => setFormData({ ...formData, gst_percentage: e.target.value })}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Leave empty for no GST"
                    />
                    {formErrors.gst_percentage && (
                      <p className="text-error text-sm mt-1">{formErrors.gst_percentage}</p>
                    )}
                  </div>
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Valid From <span className="text-error">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.valid_from}
                      onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    {formErrors.valid_from && <p className="text-error text-sm mt-1">{formErrors.valid_from}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Valid Till <span className="text-error">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.valid_till}
                      onChange={(e) => setFormData({ ...formData, valid_till: e.target.value })}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    {formErrors.valid_till && <p className="text-error text-sm mt-1">{formErrors.valid_till}</p>}
                  </div>
                </div>

                {/* Applies To */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Applies To <span className="text-error">*</span>
                  </label>
                  <select
                    value={formData.applies_to}
                    onChange={(e) => setFormData({ ...formData, applies_to: e.target.value })}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="ALL">All Services & Consultations</option>
                    <option value="WELLNESS">Wellness Services Only</option>
                    <option value="CONSULTATION">Consultations Only</option>
                  </select>
                </div>

                {/* Active Toggle */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 text-primary border-border rounded focus:ring-2 focus:ring-primary"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-foreground">
                    Enable this discount immediately
                  </label>
                </div>

                {/* Submit Error */}
                {formErrors.submit && (
                  <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="text-error" size={20} />
                    <p className="text-error">{formErrors.submit}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-4 pt-4 border-t border-border">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-6 py-3 border border-border rounded-lg hover:bg-muted/20 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    {editingDiscount ? 'Update Discount' : 'Create Discount'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DiscountManagement;

